import { useState, useEffect, useCallback } from 'react';
import type { ReactNode } from 'react';
import { Users, Plus, Pencil, Trash2, X, ChevronDown, ChevronRight } from 'lucide-react';
import PageHeader from '../../../components/PageHeader';
import WhsGate from '../../../components/whs/WhsGate';
import Button from '../../../components/ui/Button';
import whsService from '../../../services/whs';
import userService from '../../../services/user';
import type { UserData } from '../../../services/user';
import type { ManagementReview, ManagementReviewStatus } from '../../../types/whs';
import { Badge } from '../../../components/whs/whsConstants';
import { useOrgTimezone, formatLocalDateInTz } from '../../../utils/timezone';

const inputCls = 'w-full rounded-lg border border-neutral-300 px-3 py-2 text-sm focus:border-primary-500 focus:outline-none focus:ring-1 focus:ring-primary-500';
const labelCls = 'block text-xs font-medium text-neutral-600 mb-1';

const orNull = (v: string) => (v.trim() === '' ? null : v.trim());
const userLabel = (u: UserData) => [u.first_name, u.last_name].filter(Boolean).join(' ') || u.username;

const STATUS_LABEL: Record<ManagementReviewStatus, string> = { draft: 'Draft', completed: 'Completed' };
const STATUS_CLASS: Record<ManagementReviewStatus, string> = {
  draft: 'bg-neutral-100 text-neutral-700',
  completed: 'bg-emerald-100 text-emerald-800',
};

const INPUTS_HELP = 'incident & near-miss trends, audit results, objective progress, worker consultation, status of prior actions';

function Modal({ title, onClose, children, footer }: { title: string; onClose: () => void; children: ReactNode; footer?: ReactNode }) {
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4" onClick={onClose}>
      <div className="w-full max-w-2xl max-h-[90vh] overflow-y-auto rounded-xl bg-white shadow-xl" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between border-b border-neutral-200 px-5 py-3">
          <h3 className="text-base font-semibold text-neutral-900">{title}</h3>
          <button onClick={onClose} className="text-neutral-400 hover:text-neutral-600" aria-label="Close"><X className="h-5 w-5" /></button>
        </div>
        <div className="px-5 py-4">{children}</div>
        {footer && <div className="flex justify-end gap-2 border-t border-neutral-200 px-5 py-3">{footer}</div>}
      </div>
    </div>
  );
}

interface ReviewForm {
  title: string;
  review_date: string;
  period_start: string;
  period_end: string;
  inputs: string;
  outputs: string;
  decisions: string;
  chaired_by: string;
  status: ManagementReviewStatus;
  attendees: string[];
}

const emptyForm: ReviewForm = {
  title: '', review_date: '', period_start: '', period_end: '', inputs: '',
  outputs: '', decisions: '', chaired_by: '', status: 'draft', attendees: [],
};

const attendeeNames = (attendees: unknown[]): string[] =>
  attendees.map((a) => (typeof a === 'string' ? a : (a as { name?: string })?.name ?? '')).filter(Boolean);

function ManagementReviewsInner() {
  const orgTz = useOrgTimezone();

  const [reviews, setReviews] = useState<ManagementReview[]>([]);
  const [users, setUsers] = useState<UserData[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [expanded, setExpanded] = useState<number | null>(null);

  const [modalOpen, setModalOpen] = useState(false);
  const [editing, setEditing] = useState<ManagementReview | null>(null);
  const [form, setForm] = useState<ReviewForm>(emptyForm);
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    const res = await whsService.listManagementReviews();
    if (res.success) { setReviews(res.data); setError(null); } else setError(res.error.message);
    setLoading(false);
  }, []);

  const loadUsers = useCallback(async () => {
    const res = await userService.getUsers();
    if (res.success) setUsers(res.data);
  }, []);

  useEffect(() => { load(); }, [load]);
  useEffect(() => { loadUsers(); }, [loadUsers]);

  const openCreate = () => { setEditing(null); setForm(emptyForm); setModalOpen(true); };
  const openEdit = (r: ManagementReview) => {
    setEditing(r);
    setForm({
      title: r.title,
      review_date: r.review_date ?? '',
      period_start: r.period_start ?? '',
      period_end: r.period_end ?? '',
      inputs: r.inputs ?? '',
      outputs: r.outputs ?? '',
      decisions: r.decisions ?? '',
      chaired_by: r.chaired_by != null ? String(r.chaired_by) : '',
      status: r.status,
      attendees: attendeeNames(r.attendees ?? []),
    });
    setModalOpen(true);
  };

  const submit = async () => {
    if (!form.title.trim()) { setError('Title is required.'); return; }
    setSaving(true);
    const payload: Partial<ManagementReview> = {
      title: form.title.trim(),
      review_date: orNull(form.review_date),
      period_start: orNull(form.period_start),
      period_end: orNull(form.period_end),
      inputs: orNull(form.inputs),
      outputs: orNull(form.outputs),
      decisions: orNull(form.decisions),
      chaired_by: form.chaired_by ? Number(form.chaired_by) : null,
      status: form.status,
      attendees: form.attendees.map((a) => a.trim()).filter(Boolean),
    };
    const res = editing
      ? await whsService.updateManagementReview(editing.id, payload)
      : await whsService.createManagementReview(payload);
    setSaving(false);
    if (res.success) { setModalOpen(false); setError(null); await load(); }
    else setError(res.error.message);
  };

  const remove = async (r: ManagementReview) => {
    if (!window.confirm(`Delete management review "${r.title}"?`)) return;
    const res = await whsService.deleteManagementReview(r.id);
    if (res.success) await load(); else setError(res.error.message);
  };

  const period = (r: ManagementReview) => {
    if (!r.period_start && !r.period_end) return null;
    const s = r.period_start ? formatLocalDateInTz(r.period_start, orgTz) : '?';
    const e = r.period_end ? formatLocalDateInTz(r.period_end, orgTz) : '?';
    return `${s} – ${e}`;
  };

  const updateAttendee = (i: number, value: string) =>
    setForm({ ...form, attendees: form.attendees.map((a, idx) => (idx === i ? value : a)) });

  return (
    <div className="min-h-screen bg-gray-50">
      <PageHeader
        title="Management Reviews"
        subtitle="ISO 45001 §9.3 management review records — inputs, outputs & decisions"
        icon={<Users className="h-6 w-6 text-white" />}
      />

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {error && (
          <div className="mb-6 rounded-lg bg-red-50 border border-red-200 p-4 text-sm text-red-700">{error}</div>
        )}

        <div className="mb-6 flex justify-end">
          <Button onClick={openCreate}><Plus className="h-4 w-4" /> New review</Button>
        </div>

        {loading ? (
          <p className="text-center text-neutral-400">Loading…</p>
        ) : reviews.length === 0 ? (
          <p className="text-center text-neutral-400">No management reviews yet.</p>
        ) : (
          <div className="space-y-3">
            {reviews.map((r) => {
              const isOpen = expanded === r.id;
              const p = period(r);
              return (
                <div key={r.id} className="rounded-xl border border-neutral-200 bg-white">
                  <div className="flex items-center justify-between gap-3 px-5 py-4">
                    <button onClick={() => setExpanded(isOpen ? null : r.id)} className="flex flex-1 items-center gap-3 text-left">
                      {isOpen ? <ChevronDown className="h-4 w-4 text-neutral-400" /> : <ChevronRight className="h-4 w-4 text-neutral-400" />}
                      <div>
                        <div className="flex items-center gap-2">
                          <h3 className="text-base font-semibold text-neutral-900">{r.title}</h3>
                          <Badge label={STATUS_LABEL[r.status]} className={STATUS_CLASS[r.status]} />
                        </div>
                        <p className="mt-0.5 text-xs text-neutral-500">
                          {r.review_date ? formatLocalDateInTz(r.review_date, orgTz) : 'No date'}
                          {p && <span> • Period: {p}</span>}
                          {(r.chair_first_name || r.chair_last_name) && <span> • Chair: {[r.chair_first_name, r.chair_last_name].filter(Boolean).join(' ')}</span>}
                        </p>
                      </div>
                    </button>
                    <div className="flex gap-1">
                      <button onClick={() => openEdit(r)} className="rounded p-1.5 text-neutral-500 hover:bg-neutral-100 hover:text-neutral-800" aria-label="Edit"><Pencil className="h-4 w-4" /></button>
                      <button onClick={() => remove(r)} className="rounded p-1.5 text-neutral-500 hover:bg-red-50 hover:text-red-600" aria-label="Delete"><Trash2 className="h-4 w-4" /></button>
                    </div>
                  </div>

                  {isOpen && (
                    <div className="space-y-3 border-t border-neutral-100 px-5 py-4 text-sm">
                      {attendeeNames(r.attendees ?? []).length > 0 && (
                        <div>
                          <p className="font-medium text-neutral-700">Attendees</p>
                          <p className="text-neutral-600">{attendeeNames(r.attendees ?? []).join(', ')}</p>
                        </div>
                      )}
                      <div>
                        <p className="font-medium text-neutral-700">Inputs</p>
                        <p className="whitespace-pre-wrap text-neutral-600">{r.inputs || '—'}</p>
                      </div>
                      <div>
                        <p className="font-medium text-neutral-700">Outputs</p>
                        <p className="whitespace-pre-wrap text-neutral-600">{r.outputs || '—'}</p>
                      </div>
                      <div>
                        <p className="font-medium text-neutral-700">Decisions</p>
                        <p className="whitespace-pre-wrap text-neutral-600">{r.decisions || '—'}</p>
                      </div>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </div>

      {modalOpen && (
        <Modal
          title={editing ? 'Edit management review' : 'New management review'}
          onClose={() => setModalOpen(false)}
          footer={(
            <>
              <Button variant="secondary" onClick={() => setModalOpen(false)}>Cancel</Button>
              <Button onClick={submit} isLoading={saving}>{editing ? 'Save changes' : 'Create'}</Button>
            </>
          )}
        >
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
            <div className="sm:col-span-2">
              <label className={labelCls}>Title *</label>
              <input className={inputCls} value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} />
            </div>
            <div>
              <label className={labelCls}>Review date</label>
              <input type="date" className={inputCls} value={form.review_date} onChange={(e) => setForm({ ...form, review_date: e.target.value })} />
            </div>
            <div>
              <label className={labelCls}>Status</label>
              <select className={inputCls} value={form.status} onChange={(e) => setForm({ ...form, status: e.target.value as ManagementReviewStatus })}>
                {(Object.entries(STATUS_LABEL) as [ManagementReviewStatus, string][]).map(([k, v]) => (
                  <option key={k} value={k}>{v}</option>
                ))}
              </select>
            </div>
            <div>
              <label className={labelCls}>Period start</label>
              <input type="date" className={inputCls} value={form.period_start} onChange={(e) => setForm({ ...form, period_start: e.target.value })} />
            </div>
            <div>
              <label className={labelCls}>Period end</label>
              <input type="date" className={inputCls} value={form.period_end} onChange={(e) => setForm({ ...form, period_end: e.target.value })} />
            </div>
            <div className="sm:col-span-2">
              <label className={labelCls}>Chaired by</label>
              <select className={inputCls} value={form.chaired_by} onChange={(e) => setForm({ ...form, chaired_by: e.target.value })}>
                <option value="">— Unassigned —</option>
                {users.map((u) => <option key={u.id} value={u.id}>{userLabel(u)}</option>)}
              </select>
            </div>
            <div className="sm:col-span-2">
              <label className={labelCls}>Inputs</label>
              <textarea rows={3} className={inputCls} value={form.inputs} onChange={(e) => setForm({ ...form, inputs: e.target.value })} />
              <p className="mt-1 text-xs text-neutral-400">{INPUTS_HELP}</p>
            </div>
            <div className="sm:col-span-2">
              <label className={labelCls}>Outputs</label>
              <textarea rows={3} className={inputCls} value={form.outputs} onChange={(e) => setForm({ ...form, outputs: e.target.value })} />
            </div>
            <div className="sm:col-span-2">
              <label className={labelCls}>Decisions</label>
              <textarea rows={3} className={inputCls} value={form.decisions} onChange={(e) => setForm({ ...form, decisions: e.target.value })} />
            </div>
            <div className="sm:col-span-2">
              <label className={labelCls}>Attendees</label>
              <div className="space-y-2">
                {form.attendees.map((a, i) => (
                  <div key={i} className="flex gap-2">
                    <input className={inputCls} placeholder="Name" value={a} onChange={(e) => updateAttendee(i, e.target.value)} />
                    <button type="button" onClick={() => setForm({ ...form, attendees: form.attendees.filter((_, idx) => idx !== i) })} className="shrink-0 rounded p-2 text-neutral-400 hover:bg-red-50 hover:text-red-600" aria-label="Remove"><Trash2 className="h-4 w-4" /></button>
                  </div>
                ))}
                <button type="button" onClick={() => setForm({ ...form, attendees: [...form.attendees, ''] })} className="text-sm font-medium text-primary-600 hover:text-primary-700">+ Add attendee</button>
              </div>
            </div>
          </div>
        </Modal>
      )}
    </div>
  );
}

export default function WhsManagementReviews() {
  return <WhsGate><ManagementReviewsInner /></WhsGate>;
}
